/// <amd-module name="Repos/Changes/Components/Diff/DiffBlock" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { FileDiffBlockChangeType, IFileCharDiffBlock, IFileDiff, IFileDiffBlock } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation, DiffViewerSide } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    DiffSummaryDiscussionWidgetIds,
    IDiffSummaryDiscussionWidget,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { Observer } from "VSSUI/Observer";
import { css, KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { getLineCommentPosition } from "../../Util";
import { DiffViewerContext, DiffViewerSideContext } from "./DiffContext";

export const maxLinesRendered = 500;
const lineHeight = 14;

export interface IDiffSummaryDiscussionWidgetProvider {
    addComment?: (lineInfo: ILineInfo, addToOriginalDiff: boolean) => void;
    canAddComments: () => boolean;
    commentsSupported: () => boolean;
    getDisplayLineNumber: (lineNumber: number, side: DiffViewerSide) => number | undefined;
    getWidgets: () => IReadonlyObservableArray<IDiffSummaryDiscussionWidget> | IDiffSummaryDiscussionWidget[];
    getWidgetsByLine: (lineNumber: number, orientation: DiffViewerOrientation, addToOriginalDiff: boolean) => IDiffSummaryDiscussionWidget[];
    getWidgetsByLineRange: (
        lineNumber: number,
        size: number,
        orientation: DiffViewerOrientation,
        addToOriginalDiff: boolean
    ) => IDiffSummaryDiscussionWidget[];
    initializeLineMapping: (diff: IFileDiff, orientation: DiffViewerOrientation) => void;
    renderAddCommentWidget: (lineInfo: ILineInfo, addToOriginalDiff: boolean, className: string) => JSX.Element | undefined;
}

interface IDiffBlockProps extends React.PropsWithChildren<{}> {
    fileName: string;
    currentLinesRendered: number;
    displayLineNumber: number;
    isCommentedLine?: boolean;
    line?: string;
    lineLength: number;
    lineClass?: string;
    lineCount?: number;
    mLineNumber?: number;
    mLinesExist?: boolean;
    oLineNumber?: number;
    oLinesExist?: boolean;
    style?: React.CSSProperties;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}

export function createDiffId(fileName: string, oLineNumber: number | undefined, mLineNumber: number | undefined): string {
    const prefix = oLineNumber ? "o-" : "m-";
    const lineNumber = oLineNumber || mLineNumber;
    return `${prefix}diff-widget-${fileName}-${lineNumber}`;
}

const DiffBlock = (props: IDiffBlockProps): JSX.Element => {
    const {
        fileName,
        currentLinesRendered,
        children,
        displayLineNumber,
        isCommentedLine,
        lineLength,
        lineClass,
        lineCount = 1,
        mLineNumber,
        mLinesExist,
        oLineNumber,
        oLinesExist,
        style,
        widgetProvider
    } = props;

    const lineNumberAriaLabel = getLineNumberAriaLabel({
        oLinesExist,
        oLineNumber,
        mLinesExist,
        mLineNumber,
        isCommentedLine
    });

    const diffContext = React.useContext(DiffViewerContext);
    const diffSideContext = React.useContext(DiffViewerSideContext);

    const isOriginal = diffSideContext.side === DiffViewerSide.Original;

    if (widgetProvider && widgetProvider.commentsSupported()) {
        const discussionWidgets: IDiffSummaryDiscussionWidget[] = [];

        if (diffContext.orientation === DiffViewerOrientation.Inline) {
            oLineNumber &&
                discussionWidgets.push(...widgetProvider.getWidgetsByLineRange(displayLineNumber, lineCount, diffContext.orientation, true));
            mLineNumber &&
                discussionWidgets.push(...widgetProvider.getWidgetsByLineRange(displayLineNumber, lineCount, diffContext.orientation, false));
        } else {
            discussionWidgets.push(...widgetProvider.getWidgetsByLineRange(displayLineNumber, lineCount, diffContext.orientation, isOriginal));
        }

        const canAddComments = widgetProvider.canAddComments();
        const showAddComment = new ObservableValue(false);
        const id = createDiffId(fileName, oLineNumber, mLineNumber);

        const onAddComment = (
            event?: React.MouseEvent<HTMLSpanElement, MouseEvent> | React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            if (widgetProvider && widgetProvider.addComment) {
                const realLineNumber = mLineNumber ? mLineNumber : oLineNumber!;
                widgetProvider.addComment(
                    {
                        lineEnd: realLineNumber,
                        lineEndOffset: lineLength + 1,
                        lineStart: realLineNumber,
                        lineStartOffset: 1,
                        type: LineInfoType.Actual
                    },
                    mLineNumber ? false : true
                );

                if (event) {
                    event.stopPropagation();
                    event.preventDefault();
                }
            }
        };

        return (
            <>
                <DiffContentsRow
                    onMouseEnter={canAddComments ? () => (showAddComment.value = true) : undefined}
                    onMouseLeave={canAddComments ? () => (showAddComment.value = false) : undefined}
                >
                    <FocusGroupContext.Consumer>
                        {focusGroupContext => (
                            <FocusZoneContext.Consumer>
                                {zoneContext => {
                                    return (
                                        <span role="cell" className="repos-add-comment-widget text-center">
                                            <span
                                                id={id}
                                                data-focuszone={zoneContext.focuszoneId}
                                                tabIndex={getTabIndex({ id: id, tabIndex: 0 }, focusGroupContext)}
                                                onClick={event => {
                                                    onAddComment(event);
                                                }}
                                                onFocus={() => focusGroupContext.onFocus(id)}
                                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
                                                    if (event.keyCode === KeyCode.enter) {
                                                        onAddComment(event);
                                                    }
                                                }}
                                                role="button"
                                            >
                                                <span className="screen-reader-only">{Resources.AddCommentAriaLabel}</span>
                                                {(oLineNumber || mLineNumber) && (
                                                    <Observer addComment={showAddComment}>
                                                        {(observableProps: { addComment: boolean }) => {
                                                            const realLineNumber = mLineNumber ? mLineNumber : oLineNumber!;

                                                            return observableProps.addComment
                                                                ? widgetProvider.renderAddCommentWidget(
                                                                      {
                                                                          lineEnd: realLineNumber,
                                                                          lineEndOffset: lineLength + 1,
                                                                          lineStart: realLineNumber,
                                                                          lineStartOffset: 1,
                                                                          type: LineInfoType.Actual
                                                                      },
                                                                      mLineNumber ? false : true,
                                                                      "fit-button"
                                                                  )
                                                                : null;
                                                        }}
                                                    </Observer>
                                                )}
                                            </span>
                                        </span>
                                    );
                                }}
                            </FocusZoneContext.Consumer>
                        )}
                    </FocusGroupContext.Consumer>
                    <LineNumber renderLine={oLinesExist} lineNumber={oLineNumber} ariaLabel={oLinesExist ? lineNumberAriaLabel : undefined} />
                    <LineNumber renderLine={mLinesExist} lineNumber={mLineNumber} ariaLabel={!oLinesExist ? lineNumberAriaLabel : undefined} />
                    <span aria-hidden="true" className="repos-collapsed-comment"></span>
                    <LineContent lineClass={lineClass} style={style} data-oline={oLineNumber} data-mline={mLineNumber}>
                        {props.children}
                    </LineContent>
                </DiffContentsRow>
                {discussionWidgets.map((widget: IDiffSummaryDiscussionWidget, index: number) => {
                    return (
                        <DiffContentsRow isMonoSpaced={false} key={`diff-contents-row-${index}`}>
                            <span role="cell" />
                            {oLinesExist && <span aria-hidden="true" />}
                            {mLinesExist && <span aria-hidden="true" />}
                            <span role="cell" />
                            <div className="body-m repos-diff-discussion" role="cell">
                                {widget.render && widget.render()}
                            </div>
                        </DiffContentsRow>
                    );
                })}
            </>
        );
    } else {
        return (
            <DiffContentsRow>
                <LineNumber renderLine={oLinesExist} lineNumber={oLineNumber} ariaLabel={oLinesExist ? lineNumberAriaLabel : undefined} />
                <LineNumber renderLine={mLinesExist} lineNumber={mLineNumber} ariaLabel={!oLinesExist ? lineNumberAriaLabel : undefined} />
                <LineContent lineClass={lineClass} style={style}>
                    {props.children}
                </LineContent>
            </DiffContentsRow>
        );
    }
};

const DiffContentsRow = (props: {
    children?: React.ReactNode;
    isMonoSpaced?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}): JSX.Element => {
    const rowElement = React.createRef<HTMLDivElement>();

    return (
        <div
            className={css("repos-diff-contents-row", props.isMonoSpaced !== false && "monospaced-text")}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            onFocus={props.onMouseEnter}
            onBlur={props.onMouseLeave}
            ref={rowElement}
            role="row"
        >
            {props.children}
        </div>
    );
};

function getLineClassAriaLabel(lineClass?: string): string | undefined {
    let lineClassAriaLabel: string | undefined = undefined;
    if (lineClass === "added") {
        lineClassAriaLabel = Resources.Plus + " ";
    } else if (lineClass === "removed") {
        lineClassAriaLabel = Resources.Minus + " ";
    }

    return lineClassAriaLabel;
}

function getLineNumberAriaLabel(props: {
    oLineNumber?: number;
    oLinesExist?: boolean;
    mLineNumber?: number;
    mLinesExist?: boolean;
    isCommentedLine?: boolean;
}): string | undefined {
    const { oLineNumber, oLinesExist, mLineNumber, mLinesExist, isCommentedLine } = props;
    let lineNumbers: string | undefined = undefined;

    if (oLinesExist) {
        lineNumbers = oLineNumber?.toString();
    }
    if (mLinesExist && mLineNumber !== oLineNumber && mLineNumber != undefined) {
        // if orig line number != modified line number, and modified line number will render
        lineNumbers = lineNumbers ? lineNumbers + " " + mLineNumber : mLineNumber.toString();
    }

    let ariaLabel: string | undefined = undefined;

    if (lineNumbers !== undefined) {
        ariaLabel = isCommentedLine ? Resources.Commented + " " : "";
        ariaLabel += lineNumbers;
    }

    if (ariaLabel) {
        ariaLabel += " ";
    }

    return ariaLabel;
}

const LineNumber = (props: { lineNumber?: number; renderLine?: boolean; ariaLabel?: string }): JSX.Element | null => {
    return props.renderLine ? (
        <span aria-hidden={props.ariaLabel ? undefined : "true"} role="cell" className="padding-horizontal-8 text-right secondary-text">
            <span className={"screen-reader-only"}>{props.ariaLabel}</span>
            <span aria-hidden="true">
                <span className="repos-line-number" data-line={props.lineNumber}></span>
            </span>
        </span>
    ) : null;
};

const LineContent = ({
    children,
    lineClass,
    ...otherProps
}: {
    children?: React.ReactNode;
    lineClass?: string;
    style?: React.CSSProperties;
}): JSX.Element => {
    const lineClassAria = getLineClassAriaLabel(lineClass);
    return (
        <span className={css("padding-right-8 repos-line-content", lineClass)} {...otherProps} role="cell">
            {lineClassAria && <span className="screen-reader-only">{lineClassAria}&nbsp;</span>}
            <span aria-hidden="true">
                <span className={css("line-icon", lineClass)}></span>
            </span>
            {children || <span className="empty-line" />}
        </span>
    );
};

interface ICharDiffBlockProps {
    fileName: string;
    currentLinesRendered: number;
    changeType: FileDiffBlockChangeType;
    charChanges: IFileDiffBlock[];
    commentStart?: number;
    commentEnd?: number;
    displayLineNumber: number;
    endLine: number;
    line: string;
    lineClass: string;
    mLineNumber?: number;
    mLinesExist?: boolean;
    oLineNumber?: number;
    oLinesExist?: boolean;
    startLine: number;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}

const CharDiffBlock = (props: ICharDiffBlockProps): JSX.Element => {
    const {
        fileName,
        currentLinesRendered,
        changeType,
        displayLineNumber,
        endLine,
        line,
        lineClass,
        mLineNumber,
        mLinesExist,
        oLineNumber,
        oLinesExist,
        startLine
    } = props;

    let spotInLine = 0;
    let isCommentedLine = false;

    const diffChildren: JSX.Element[] = [];
    for (let i = 0; i < props.charChanges.length; i++) {
        const diffStart = changeType === FileDiffBlockChangeType.Delete ? props.charChanges[i].oLine : props.charChanges[i].mLine;
        const diffCount = changeType === FileDiffBlockChangeType.Delete ? props.charChanges[i].oLinesCount : props.charChanges[i].mLinesCount;

        // We've already completely passed this change (via comments walking us beyond it) OR change doesn't intersect with this line OR change is empty; disregard it
        if (diffStart + diffCount < startLine + spotInLine || diffStart >= endLine || diffStart + diffCount == 0) {
            continue;
        }

        // Calculate all of our positions relative to 0-based index into the props.line string
        // Simplifies math and tracking a lot below
        const lineRelativePositions = {
            commentStart: props.commentStart,
            commentEnd: props.commentEnd,
            diffStart: diffStart - startLine,
            diffEnd: diffStart - startLine + diffCount
        };

        // Get the first point that interrupts unchanged text and calculate some info for it
        const firstInterrupt =
            lineRelativePositions.commentStart !== undefined && spotInLine <= lineRelativePositions.commentStart
                ? Math.min(lineRelativePositions.diffStart, lineRelativePositions.commentStart)
                : lineRelativePositions.diffStart;
        const isInterruptComment = firstInterrupt === lineRelativePositions.commentStart;
        let interruptEnd: number;

        if (isInterruptComment) {
            // Comments trump changes, so always take the comment end
            interruptEnd = lineRelativePositions.commentEnd!;
        } else {
            // Check if a comment starts before the end of this change
            interruptEnd =
                lineRelativePositions.commentStart !== undefined && spotInLine <= lineRelativePositions.commentStart
                    ? Math.min(lineRelativePositions.diffEnd, lineRelativePositions.commentStart)
                    : lineRelativePositions.diffEnd;
        }

        // If we're beyond the current start of the interrupt, use our current location to calc
        const interruptLength = interruptEnd - Math.max(firstInterrupt, spotInLine);

        // Precompute this classname for ease of use
        const changeTypeClassName = changeType === FileDiffBlockChangeType.Delete ? "removed-content" : "added-content";

        // If this interrupt is beyond our current location, push an unchanged span up to the interrupt point
        if (firstInterrupt > spotInLine) {
            diffChildren.push(<span key={"line-start-" + i}>{line.substr(spotInLine, firstInterrupt - spotInLine) || ""}</span>);
            spotInLine = firstInterrupt;
        }

        if (interruptLength > 0) {
            if (isInterruptComment) {
                isCommentedLine = true;
            }
            diffChildren.push(
                <span key={"line-interrupt-" + i} className={isInterruptComment ? "diff-comment" : changeTypeClassName} data-offset={spotInLine}>
                    {line.substr(spotInLine, interruptLength)}
                </span>
            );
            spotInLine = interruptEnd;
        }

        // If we just rendered a comment as our interrupt, check if the current diff continues beyond our current position and render the rest with the right style
        // Catchall below won't handle it, but will handle dangling comments
        if (isInterruptComment && lineRelativePositions.diffEnd > spotInLine) {
            diffChildren.push(
                <span key={"line-remaining-diff-" + i} className={changeTypeClassName}>
                    {line.substr(spotInLine, lineRelativePositions.diffEnd - spotInLine)}
                </span>
            );
            spotInLine = lineRelativePositions.diffEnd;
        }
    }

    if (spotInLine !== line.length) {
        if (props.commentStart !== undefined && props.commentEnd !== undefined && props.commentStart < line.length && props.commentEnd > spotInLine) {
            let commentEnd: number = props.commentEnd;
            if (commentEnd > line.length) {
                commentEnd = line.length;
            }

            let commentStart: number = props.commentStart;
            if (commentStart < spotInLine) {
                commentStart = spotInLine;
            }

            if (commentStart !== spotInLine) {
                diffChildren.push(<span key="line-start">{props.line.substr(spotInLine, commentStart - spotInLine) || ""}</span>);
            }

            isCommentedLine = true;
            diffChildren.push(
                <span key="diff-comment-end" className="diff-comment">
                    {line.substr(commentStart, commentEnd - commentStart) || ""}
                </span>
            );

            if (commentEnd !== line.length) {
                diffChildren.push(<span key="remaining-end">{line.substr(commentEnd) || ""}</span>);
            }
        } else {
            diffChildren.push(<span key="remaining-line">{line.substr(spotInLine) || ""}</span>);
        }
    }

    return (
        <DiffBlock
            fileName={fileName}
            currentLinesRendered={currentLinesRendered}
            displayLineNumber={displayLineNumber}
            isCommentedLine={isCommentedLine}
            lineLength={line.length}
            lineClass={lineClass}
            mLineNumber={mLineNumber}
            mLinesExist={mLinesExist}
            oLineNumber={oLineNumber}
            oLinesExist={oLinesExist}
            widgetProvider={props.widgetProvider}
        >
            {...diffChildren}
        </DiffBlock>
    );
};

export function processCharDiffChanges(
    fileName: string,
    changeType: FileDiffBlockChangeType,
    currentLinesRendered: number,
    currentDisplayLine: number,
    fileCharDiffBlock: IFileCharDiffBlock,
    lines: string[],
    oLineNumber: number | undefined,
    mLineNumber: number | undefined,
    lineClass: string,
    oLinesExist: boolean,
    mLinesExist: boolean,
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider,
    commentPositions?: CommentThreadContext[]
): JSX.Element[] {
    const results: JSX.Element[] = [];
    let charCount = 0;
    for (let i = 0; i < lines.length; i++) {
        if (currentLinesRendered + 1 >= maxLinesRendered) {
            break;
        }

        const line = lines[i];
        const endLine = charCount + line.length;

        let commentStart: number | undefined;
        let commentEnd: number | undefined;
        if (commentPositions) {
            const commentPosition = getLineCommentPosition(commentPositions, line.length, oLineNumber, mLineNumber);
            commentStart = commentPosition.start;
            commentEnd = commentPosition.end;
        }

        if (fileCharDiffBlock.charChange.length > 0) {
            results.push(
                <CharDiffBlock
                    fileName={fileName}
                    currentLinesRendered={currentLinesRendered}
                    key={format("char-diff-{0}-{1}", changeType, i)}
                    changeType={changeType}
                    charChanges={fileCharDiffBlock.charChange}
                    commentStart={commentStart}
                    commentEnd={commentEnd}
                    displayLineNumber={currentDisplayLine}
                    endLine={endLine}
                    line={line}
                    lineClass={lineClass}
                    mLineNumber={mLineNumber}
                    mLinesExist={mLinesExist}
                    oLineNumber={oLineNumber}
                    oLinesExist={oLinesExist}
                    startLine={charCount}
                    widgetProvider={widgetProvider}
                />
            );
        } else {
            results.push(
                <LineDiffBlock
                    fileName={fileName}
                    currentLinesRendered={currentLinesRendered}
                    key={format("line-diff-{0}-{1}", changeType, i)}
                    commentStart={commentStart}
                    commentEnd={commentEnd}
                    displayLineNumber={currentDisplayLine}
                    mLineNumber={mLineNumber}
                    mLinesExist={mLinesExist}
                    oLineNumber={oLineNumber}
                    oLinesExist={oLinesExist}
                    line={line}
                    lineClass={lineClass}
                    widgetProvider={widgetProvider}
                />
            );
        }
        charCount += line.length;

        if (mLineNumber) {
            mLineNumber++;
        }
        if (oLineNumber) {
            oLineNumber++;
        }

        currentLinesRendered += 1;
        currentDisplayLine += 1;
    }

    return results;
}

interface ILineDiffBlockProps {
    fileName: string;
    currentLinesRendered: number;
    displayLineNumber: number;
    line: string;
    commentStart?: number;
    commentEnd?: number;
    lineClass?: string;
    lineCount?: number;
    mLineNumber?: number;
    mLinesExist?: boolean;
    oLineNumber?: number;
    oLinesExist?: boolean;
    style?: React.CSSProperties;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}

const LineDiffBlock = (props: ILineDiffBlockProps): JSX.Element => {
    const { commentStart, commentEnd, line } = props;
    let isCommentedLine = false;

    if (commentStart === undefined || commentEnd === undefined) {
        return (
            <DiffBlock {...props} lineLength={line.length}>
                {props.line}
            </DiffBlock>
        );
    }

    const diffChildren: JSX.Element[] = [];
    if (commentStart !== 0) {
        diffChildren.push(<span key="line-start">{props.line.substr(0, commentStart) || ""}</span>);
    }

    let end: number = commentEnd;
    if (end > line.length) {
        end = line.length;
    }

    isCommentedLine = true;
    diffChildren.push(
        <span key="diff-comment" className="diff-comment">
            {line.substr(commentStart, end - commentStart) || ""}
        </span>
    );

    if (commentEnd !== line.length) {
        diffChildren.push(<span key={"remaining-line"}>{line.substr(commentEnd) || ""}</span>);
    }

    return (
        <DiffBlock {...props} isCommentedLine={isCommentedLine} lineLength={line.length}>
            {...diffChildren}
        </DiffBlock>
    );
};

export function getHiddenCommentCount(widgetProvider?: IDiffSummaryDiscussionWidgetProvider): number {
    let hiddenComments = 0;

    if (widgetProvider) {
        const widgets = ObservableLike.getValue(widgetProvider.getWidgets());
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i];

            if (widget.getId() === DiffSummaryDiscussionWidgetIds.Comment) {
                let lineNumber = widget.getLineNumber(true);
                if (lineNumber === SpecialCommentPositions.NotVisible) {
                    hiddenComments += widget.getThreadCount();
                }

                lineNumber = widget.getLineNumber(false);
                if (lineNumber === SpecialCommentPositions.NotVisible) {
                    hiddenComments += widget.getThreadCount();
                }
            }
        }
    }

    return hiddenComments;
}

export function processLineDiffChanges(
    fileName: string,
    currentLinesRendered: number,
    currentDisplayLine: number,
    lines: string[],
    oLineNumber: number | undefined,
    mLineNumber: number | undefined,
    lineClass: string,
    oLinesExist: boolean,
    mLinesExist: boolean,
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider,
    commentPositions?: CommentThreadContext[]
): JSX.Element[] {
    const results: JSX.Element[] = [];
    for (let i = 0; i < lines.length; i++) {
        if (currentLinesRendered + 1 >= maxLinesRendered) {
            break;
        }

        let commentStart: number | undefined;
        let commentEnd: number | undefined;
        if (commentPositions) {
            const commentPosition = getLineCommentPosition(commentPositions, lines[i].length, oLineNumber, mLineNumber);
            commentStart = commentPosition.start;
            commentEnd = commentPosition.end;
        }

        results.push(
            <LineDiffBlock
                fileName={fileName}
                currentLinesRendered={currentLinesRendered}
                commentStart={commentStart}
                commentEnd={commentEnd}
                displayLineNumber={currentDisplayLine}
                key={"line-diff-" + i}
                line={lines[i]}
                lineClass={lineClass}
                mLineNumber={mLineNumber}
                mLinesExist={mLinesExist}
                oLineNumber={oLineNumber}
                oLinesExist={oLinesExist}
                widgetProvider={widgetProvider}
            />
        );

        if (mLineNumber) {
            mLineNumber++;
        }
        if (oLineNumber) {
            oLineNumber++;
        }

        currentLinesRendered += 1;
        currentDisplayLine += 1;
    }
    return results;
}

export const EmptyLines = (props: {
    fileName: string;
    displayLineNumber: number;
    lines: number;
    oLinesExist: boolean;
    mLinesExist: boolean;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}): JSX.Element => {
    const styleProp: React.CSSProperties = {};
    styleProp.height = format("{0}px", props.lines * lineHeight);
    return (
        <LineDiffBlock
            fileName={props.fileName}
            currentLinesRendered={props.displayLineNumber}
            displayLineNumber={props.displayLineNumber}
            oLineNumber={undefined}
            oLinesExist={props.oLinesExist}
            mLineNumber={undefined}
            mLinesExist={props.mLinesExist}
            line={""}
            lineClass="filler"
            lineCount={props.lines}
            style={styleProp}
            widgetProvider={props.widgetProvider}
        />
    );
};

export const EllipsisLine = (props: {
    mLinesExist?: boolean;
    oLinesExist?: boolean;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}): JSX.Element => {
    return (
        <div className="repos-diff-contents-row repos-elliplis-row flex flex-center" aria-hidden="true">
            {props.widgetProvider && (
                <span className="padding-horizontal-8 v-align-middle">
                    <div className="flex" />
                </span>
            )}
            {props.oLinesExist && (
                <span className="padding-horizontal-8 v-align-middle">
                    <div className="flex" />
                </span>
            )}
            {props.mLinesExist && (
                <span className="padding-horizontal-8 v-align-middle">
                    <div className="flex" />
                </span>
            )}
            {props.widgetProvider && <span className="padding-horizontal-8 v-align-middle" />}
            <span className="padding-right-8 v-align-middle repos-line-content">
                <div className="flex" />
            </span>
        </div>
    );
};
